import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  Payment,
  PaymentFilters,
  PaymentConcept,
  PaginationInfo,
} from "@/types/payment.types";
import {
  fetchFilteredPurchases,
  fetchPurchases,
  fetchStudentPurchases,
  fetchPurchasesByUserIdentifier,
} from "./paymentsThunks";
import {
  fetchAddPurchaseConcept,
  fetchDeletePurchaseConcept,
  fetchUpdatePurchaseConcept,
  fetchPaymentItems,
} from "../options/optionsThunks";

interface PaymentsState {
  payments: Payment[];
  studentPurchases: Payment[];
  userPurchases: Payment[];
  paymentConcepts: PaymentConcept[];
  filters: PaymentFilters;
  pagination: PaginationInfo;
  isLoading: boolean;
  studentPurchasesLoading: boolean;
  userPurchasesLoading: boolean;
  error: string | null;
  studentPurchasesError: string | null;
  userPurchasesError: string | null;
  lastFetch: string | null;
}

const initialState: PaymentsState = {
  payments: [],
  studentPurchases: [],
  userPurchases: [],
  paymentConcepts: [],
  filters: {
    dateRange: { start: null, end: null },
    status: null,
    userId: null,
    userName: null,
    paymentItem: null,
  },
  pagination: {
    currentPage: 1,
    pageSize: 10,
    hasNextPage: false,
    hasPreviousPage: false,
    lastVisible: null,
    firstVisible: null,
    cursors: {},
    totalPagesKnown: 1,
  },
  isLoading: false,
  studentPurchasesLoading: false,
  userPurchasesLoading: false,
  error: null,
  studentPurchasesError: null,
  userPurchasesError: null,
  lastFetch: null,
};

const paymentsSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<PaymentFilters>>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
      state.pagination = initialState.pagination;
    },

    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination = initialState.pagination;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
      state.pagination.lastVisible = null;
      state.pagination.firstVisible = null;
      state.pagination.cursors = {};
      state.pagination.totalPagesKnown = 1;
    },
    setPaymentItem: (state, action: PayloadAction<string | null>) => {
      state.filters.paymentItem = action.payload;
    },
    setPaginationData: (
      state,
      action: PayloadAction<{
        page: number;
        hasNext: boolean;
        hasPrevious: boolean;
        firstDoc: any;
        lastDoc: any;
      }>
    ) => {
      const { page, hasNext, hasPrevious, firstDoc, lastDoc } = action.payload;

      state.pagination.currentPage = page;
      state.pagination.hasNextPage = hasNext;
      state.pagination.hasPreviousPage = hasPrevious;
      state.pagination.firstVisible = firstDoc;
      state.pagination.lastVisible = lastDoc;

      state.pagination.cursors[page] = { firstDoc, lastDoc };

      if (hasNext && page >= state.pagination.totalPagesKnown) {
        state.pagination.totalPagesKnown = page + 1;
      }
    },
    resetPagination: (state) => {
      state.pagination = initialState.pagination;
    },
    setPayments: (state, action: PayloadAction<Payment[]>) => {
      state.payments = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    addPayment: (state, action: PayloadAction<Payment>) => {
      state.payments.push(action.payload);
    },
    updatePaymentStatus: (
      state,
      action: PayloadAction<{ id: string; status: Payment["status"] }>
    ) => {
      const payment = state.payments.find((p) => p.id === action.payload.id);
      if (payment) {
        payment.status = action.payload.status;
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearStudentPurchases: (state) => {
      state.studentPurchases = [];
      state.studentPurchasesError = null;
      state.studentPurchasesLoading = false;
    },
    clearUserPurchases: (state) => {
      state.userPurchases = [];
      state.userPurchasesError = null;
      state.userPurchasesLoading = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch payment items
    builder.addCase(fetchPaymentItems.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchPaymentItems.fulfilled, (state, action) => {
      // promise resolves successfully
      state.paymentConcepts = action.payload;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(fetchPaymentItems.rejected, (state, action) => {
      // promise rejected
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Fetch purchases
    builder.addCase(fetchPurchases.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchPurchases.fulfilled, (state, action) => {
      state.payments = action.payload;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(fetchPurchases.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    builder.addCase(fetchFilteredPurchases.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchFilteredPurchases.fulfilled, (state, action) => {
      state.isLoading = false;
      state.payments = action.payload.data;

      const {
        currentPage,
        hasNextPage,
        hasPreviousPage,
        firstVisible,
        lastVisible,
      } = action.payload;

      state.pagination.currentPage = currentPage;
      state.pagination.hasNextPage = hasNextPage;
      state.pagination.hasPreviousPage = hasPreviousPage;
      state.pagination.firstVisible = firstVisible;
      state.pagination.lastVisible = lastVisible;

      // 👇 Guardar cursores
      state.pagination.cursors[currentPage] = {
        firstDoc: firstVisible,
        lastDoc: lastVisible,
      };
      if (hasNextPage && currentPage >= state.pagination.totalPagesKnown) {
        state.pagination.totalPagesKnown = currentPage + 1;
      }

      // Actualizar filtros
      state.filters = action.meta.arg.filters;
      state.lastFetch = new Date().toISOString();
    });
    builder.addCase(fetchFilteredPurchases.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  
		builder.addCase(fetchUpdatePurchaseConcept.pending, (state) => {
			state.isLoading = true
			state.error = null
		})
		builder.addCase(fetchUpdatePurchaseConcept.fulfilled, (state, action) => {
		
			const index = state.paymentConcepts.findIndex(c => c.id === action.payload.id)
			if (index !== -1) {
				state.paymentConcepts[index] = {...state.paymentConcepts[index], ...action.payload}
			}
			state.isLoading = false
			state.error = null
		})
		builder.addCase(fetchUpdatePurchaseConcept.rejected, (state, action) => {
			state.isLoading = false
			state.error = action.payload as string || action.error?.message || 'Failed to update payment concept'
		})

		builder.addCase(fetchAddPurchaseConcept.pending, (state) => {
			state.isLoading = true
			state.error = null
		})
		builder.addCase(fetchAddPurchaseConcept.fulfilled, (state, action: PayloadAction<PaymentConcept>) => {
		  state.isLoading = false
      state.paymentConcepts.push(action.payload)
		})
		builder.addCase(fetchAddPurchaseConcept.rejected, (state, action) => {
			state.isLoading = false
			state.error = action.payload as string || action.error?.message || 'Failed to update payment concept'
		})
		
		// Delete Purchase Concept (Eliminar)
		builder.addCase(fetchDeletePurchaseConcept.pending, (state) => {
			state.isLoading = true
			state.error = null
		})
		builder.addCase(fetchDeletePurchaseConcept.fulfilled, (state, action: PayloadAction<string>) => {
			const deletedId = action.payload
			// Elimina el concepto de pago de la lista
			state.paymentConcepts = state.paymentConcepts.filter(c => c.id !== deletedId)
			state.isLoading = false
			state.error = null
		})
		builder.addCase(fetchDeletePurchaseConcept.rejected, (state, action) => {
			state.isLoading = false
			state.error = action.payload as string
		})

		// Fetch Student Purchases
		builder.addCase(fetchStudentPurchases.pending, (state) => {
			state.studentPurchasesLoading = true
			state.studentPurchasesError = null
		})
		builder.addCase(fetchStudentPurchases.fulfilled, (state, action: PayloadAction<Payment[]>) => {
			state.studentPurchasesLoading = false
			state.studentPurchases = action.payload
			state.studentPurchasesError = null
		})
		builder.addCase(fetchStudentPurchases.rejected, (state, action) => {
			state.studentPurchasesLoading = false
			state.studentPurchasesError = action.payload as string || 'Failed to fetch student purchases'
		})

		// Fetch Purchases by User Identifier
		builder.addCase(fetchPurchasesByUserIdentifier.pending, (state) => {
			state.userPurchasesLoading = true
			state.userPurchasesError = null
		})
		builder.addCase(fetchPurchasesByUserIdentifier.fulfilled, (state, action: PayloadAction<Payment[]>) => {
			state.userPurchasesLoading = false
			state.userPurchases = action.payload
			state.userPurchasesError = null
		})
		builder.addCase(fetchPurchasesByUserIdentifier.rejected, (state, action) => {
			state.userPurchasesLoading = false
			state.userPurchasesError = action.payload as string || 'Failed to fetch user purchases'
		})
  },
});

export const {
  setPayments,
  setPageSize,
  setPaginationData,
  resetPagination,
  addPayment,
  updatePaymentStatus,
  setLoading,
  setError,
  clearError,
  setFilters,
  resetFilters,
  clearStudentPurchases,
  clearUserPurchases,
} = paymentsSlice.actions;
export default paymentsSlice.reducer;
